import api from 'services/api';
import { REJECT, array, numeric, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /locks` — see ../../README.md. Self-contained on
// purpose: its own type, its own schema, shared with nothing. The full contract for
// this route is `getLocks`, and a picker reads two of its fields.
//
// Declaring only those is what lets a picker refuse a degraded answer without
// refusing on drift it does not care about. `object()` ignores the rest of the
// payload, so nothing else is parsed and nothing else can warn — and that stays
// true when `getLocks` grows to the full response in its E-S pass, which is why
// this does not simply read through it.
//
// It shares the route with `getLocks` today; a slim backend read — less bandwidth,
// and the server-side filtering some of these lack — would replace the request and
// leave this contract standing.

/** A lock as a picker deals in it — the two parts its stored `zone_name|name` path carries. */
export interface LockSummary {
  readonly name: string;
  readonly zone_name: string;
}

const summarySchema = object<LockSummary>({
  name: str(REJECT),
  zone_name: str(),
});

interface LocksSearchResponse {
  readonly locks: readonly LockSummary[];
  readonly pagination: { readonly total_records: number };
}

const responseSchema = object<LocksSearchResponse>({
  locks: array(summarySchema),
  // COUNT-derived totals arrive as numeric strings from Postgres.
  pagination: object<{ readonly total_records: number }>({ total_records: numeric(REJECT) }),
});

export interface SearchLocksParams {
  readonly limit: number;
  readonly name?: string;
  readonly zone_name?: string;
}

/** One page of options, with the server-side count of all matches. */
export interface LocksSearchPage {
  readonly items: readonly LockSummary[];
  readonly total: number;
}

// In-flight de-duplication, keyed on the exact query: independent callers that ask
// the same question at the same time share one request rather than one each.
// Nothing is cached past settlement — asking again later is a genuinely new
// question, since the underlying records change under us.
const inFlight = new Map<string, Promise<LocksSearchPage>>();

// Key on the query's entries, sorted, so two callers that build the same query with
// their keys in a different order still share one request.
const queryKey = (query: SearchLocksParams): string =>
  JSON.stringify(
    Object.entries(query)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : 1))
  );

/**
 * One page of locks matching `params`, ordered by name.
 *
 * Name order is fixed here rather than left to callers, so that no caller can
 * present an unordered list. The backend column is `COLLATE "C"`, so it is byte
 * order: every uppercase initial sorts before every lowercase one.
 *
 * Collapses with `refuseDegraded`, and that is a property of this contract rather
 * than a policy imposed on an unknown consumer: an option missing from the list is
 * one the user cannot pick, and one they picked before no longer resolves. A
 * partial list of values is not a list of values.
 *
 * `undefined` params are dropped by axios, so omitting one widens the query rather
 * than filtering on empty.
 */
export const searchLocks = async (params: SearchLocksParams): Promise<LocksSearchPage> => {
  const key = queryKey(params);
  const pending = inFlight.get(key);
  if (pending !== undefined) return pending;

  const request = api
    .get('/locks', { params: { ...params, sortColumn: 'name', sortOrder: 'ASC' } })
    .then((res) => refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'searchLocks' })))
    .then(({ locks, pagination }) => ({ items: locks, total: pagination.total_records }))
    .finally(() => inFlight.delete(key));
  inFlight.set(key, request);
  return request;
};
